import { useState } from "react";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import { useLocation } from "react-router-dom";
import Timesheet from "./Timesheet";
import LiveActivityDashboard from "./LiveActivityDashboard";

/**
 * Combined work-tracking screen.
 *   - Desktop (md+): Timesheet on the left, Live Activity on the right.
 *   - Mobile:        two tabs to switch between them.
 *   - Roles:         every role sees both panels; the Team tab inside
 *                    Timesheet stays manager-only.
 *
 * Reachable from both /timesheet and /live-activity; when arriving via
 * /live-activity on mobile, the Live tab is preselected.
 */
const WorkTracker = () => {
  const resTheme = useTheme();
  // Two dense panels only fit comfortably on wide screens. Below ~1200px
  // (smaller laptops / tablets) fall back to the tabbed single-panel view so
  // nothing overflows behind the sidebar or gets clipped on the right.
  const isNarrow = useMediaQuery(resTheme.breakpoints.down("lg"));
  const location = useLocation();
  const arrivedOnLive = (location.pathname || "").includes("live-activity");

  const [tab, setTab] = useState(arrivedOnLive ? 1 : 0);

  // ── Mobile / narrow: two tabs ──
  if (isNarrow) {
    const TabBtn = ({ index, label }) => {
      const activeSel = tab === index;
      return (
        <Box
          onClick={() => setTab(index)}
          sx={{
            flex: 1,
            textAlign: "center",
            px: 2,
            py: 0.9,
            borderRadius: 2,
            cursor: "pointer",
            fontSize: 13.5,
            fontWeight: 700,
            color: activeSel ? "#fff" : "#667085",
            background: activeSel
              ? "linear-gradient(135deg, #102A83, #1D4ED8)"
              : "transparent",
            boxShadow: activeSel ? "0 4px 12px rgba(16,42,131,0.28)" : "none",
            transition: "all 0.2s ease",
            userSelect: "none",
          }}
        >
          {label}
        </Box>
      );
    };

    return (
      <Box sx={{ maxWidth: 940, mx: "auto", py: 2, px: 0 }}>
        <Box
          sx={{
            display: "flex",
            gap: 0.5,
            p: 0.5,
            borderRadius: 2.5,
            bgcolor: "#F2F4F7",
            mb: 2,
          }}
        >
          <TabBtn index={0} label="Timesheet" />
          <TabBtn index={1} label="Live Activity" />
        </Box>
        {tab === 0 ? (
          <Timesheet embedded />
        ) : (
          <LiveActivityDashboard embedded />
        )}
      </Box>
    );
  }

  // ── Desktop: side by side, each column scrolls independently ──
  // Bound the row to the visible area (viewport minus the app header + outer
  // padding ≈ 72px) so the page itself doesn't scroll; each column then
  // scrolls internally with its scrollbar hidden.
  const column = {
    flex: "1 1 0",
    minWidth: 0, // allow the column to shrink below its content's natural width
    width: 0, // equal split regardless of content width
    height: "100%",
    overflowY: "auto",
    overflowX: "hidden", // clip any horizontal spill instead of pushing width out
    minHeight: 0,
    "&::-webkit-scrollbar": { display: "none" }, // Chrome / Safari
    scrollbarWidth: "none", // Firefox
    msOverflowStyle: "none", // IE / Edge
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "stretch",
        gap: 4,
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
        height: "calc(100vh - 72px)",
        overflow: "hidden",
        py: 2.5,
        px: 0,
      }}
    >
      <Box sx={column}>
        <Timesheet embedded />
      </Box>
      <Box sx={column}>
        <LiveActivityDashboard embedded />
      </Box>
    </Box>
  );
};

export default WorkTracker;
